import { alpha } from '@mui/material/styles';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { gray, brand } from '../../getDesignTokens';

/**
 * MuiCheckbox — replaces MUI's default SVG icons with rounded variants
 * and repaints the checkbox as a bordered square that fills brand[500]
 * on check. Sized to 16×16 to match dense form rows.
 *
 * `defaultProps.icon / checkedIcon / indeterminateIcon` swap MUI's
 * bundled shapes for the rounded set. Every Checkbox in the app gets
 * these unless a caller overrides them explicitly.
 */
export const checkboxCustomizations = {
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
      icon: (
        <CheckBoxOutlineBlankRoundedIcon sx={{ color: 'hsla(210, 0%, 0%, 0.0)' }} />
      ),
      checkedIcon: <CheckRoundedIcon sx={{ height: 14, width: 14 }} />,
      indeterminateIcon: <RemoveRoundedIcon sx={{ height: 14, width: 14 }} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 10,
        height: 16,
        width: 16,
        // The 16px box is the checkbox. MUI's own 9px padding left it no room,
        // so the icon drew at 24px over the box — a second, larger square that
        // the grid's header row clipped, and read as half a checkbox.
        padding: 0,
        '& .MuiSvgIcon-root': { width: 14, height: 14 },
        // Unchecked, the box's border is the outline; the icon's own square
        // must not show. Its transparent colour was not reaching the SVG.
        '&:not(.Mui-checked):not(.MuiCheckbox-indeterminate) .MuiSvgIcon-root': { opacity: 0 },
        borderRadius: 5,
        border: '1px solid ',
        borderColor: alpha(gray[300], 0.8),
        boxShadow: '0 0 0 1.5px hsla(210, 0%, 0%, 0.04) inset',
        backgroundColor: alpha(gray[100], 0.4),
        transition: 'border-color, background-color, 120ms ease-in',
        '&:hover': {
          borderColor: brand[300],
        },
        '&.Mui-focusVisible': {
          outline: `3px solid ${alpha(brand[500], 0.5)}`,
          outlineOffset: '2px',
          borderColor: brand[400],
        },
        '&.Mui-checked': {
          color: 'white',
          backgroundColor: brand[500],
          borderColor: brand[500],
          boxShadow: `none`,
          '&:hover': {
            backgroundColor: brand[600],
          },
        },
        ...theme.applyStyles('dark', {
          borderColor: alpha(gray[700], 0.8),
          boxShadow: '0 0 0 1.5px hsl(210, 0%, 0%) inset',
          backgroundColor: alpha(gray[900], 0.8),
          '&:hover': {
            borderColor: brand[300],
          },
          '&.Mui-focusVisible': {
            borderColor: brand[400],
            outline: `3px solid ${alpha(brand[500], 0.5)}`,
            outlineOffset: '2px',
          },
        }),
      }),
    },
  },
};
